import type { StoneFinding, StoneFindingResponse } from "./types";

export class StoneContractError extends Error {
  constructor(message: string) {
    super(`Stone contract violation: ${message}`);
    this.name = "StoneContractError";
  }
}

function num(v: unknown, path: string): number {
  if (typeof v !== "number" || Number.isNaN(v))
    throw new StoneContractError(`${path} must be a number`);
  return v;
}

function str(v: unknown, path: string): string {
  if (typeof v !== "string" || v.length === 0)
    throw new StoneContractError(`${path} must be a non-empty string`);
  return v;
}

function bbox(v: unknown, path: string) {
  if (typeof v !== "object" || v === null) throw new StoneContractError(`${path} missing`);
  const o = v as Record<string, unknown>;
  return {
    x: num(o.x, `${path}.x`),
    y: num(o.y, `${path}.y`),
    w: num(o.w, `${path}.w`),
    h: num(o.h, `${path}.h`),
  };
}

function point(v: unknown, path: string): [number, number] {
  if (!Array.isArray(v) || v.length !== 2) throw new StoneContractError(`${path} must be [x,y]`);
  return [num(v[0], `${path}[0]`), num(v[1], `${path}[1]`)];
}

function segment(v: unknown, path: string): [[number, number], [number, number]] {
  if (!Array.isArray(v) || v.length !== 2)
    throw new StoneContractError(`${path} must be two points`);
  return [point(v[0], `${path}[0]`), point(v[1], `${path}[1]`)];
}

function parseStone(raw: unknown, i: number): StoneFinding {
  if (typeof raw !== "object" || raw === null)
    throw new StoneContractError(`stones[${i}] not an object`);
  const r = raw as Record<string, unknown>;
  const m = (r.measurements ?? {}) as Record<string, unknown>;
  const hu = (r.hu ?? {}) as Record<string, unknown>;
  if (typeof r.id !== "string") throw new StoneContractError(`stones[${i}].id missing`);
  return {
    id: r.id,
    seriesInstanceUid: str(r.seriesInstanceUid, `stones[${i}].seriesInstanceUid`),
    seriesNumber: num(r.seriesNumber, `stones[${i}].seriesNumber`),
    sliceIndex: num(r.sliceIndex, `stones[${i}].sliceIndex`),
    sliceNumber: num(r.sliceNumber, `stones[${i}].sliceNumber`),
    frameOfReferenceUid: (r.frameOfReferenceUid as string | null) ?? null,
    measurements: {
      longAxisMm: num(m.longAxisMm, `stones[${i}].measurements.longAxisMm`),
      shortAxisMm: num(m.shortAxisMm, `stones[${i}].measurements.shortAxisMm`),
      volumeMm3: num(m.volumeMm3, `stones[${i}].measurements.volumeMm3`),
    },
    hu: {
      max: num(hu.max, `stones[${i}].hu.max`),
      mean: num(hu.mean, `stones[${i}].hu.mean`),
      min: typeof hu.min === "number" ? hu.min : undefined,
    },
    cropBoundsPx: bbox(r.cropBoundsPx, `stones[${i}].cropBoundsPx`),
    longAxisPx: segment(r.longAxisPx, `stones[${i}].longAxisPx`),
    shortAxisPx: segment(r.shortAxisPx, `stones[${i}].shortAxisPx`),
    worldCentroid: r.worldCentroid as StoneFinding["worldCentroid"],
    segmentIndex: typeof r.segmentIndex === "number" ? r.segmentIndex : undefined,
    pixelCentroid:
      Array.isArray(r.pixelCentroid) && r.pixelCentroid.length === 2
        ? [
            num(r.pixelCentroid[0], `stones[${i}].pixelCentroid[0]`),
            num(r.pixelCentroid[1], `stones[${i}].pixelCentroid[1]`),
          ]
        : undefined,
    confidence: num(r.confidence, `stones[${i}].confidence`),
    laterality: (r.laterality as StoneFinding["laterality"]) ?? null,
    location: (r.location as StoneFinding["location"]) ?? null,
    maskS3Path: typeof r.maskS3Path === "string" ? r.maskS3Path : undefined,
  };
}

export function parseStoneFindingsResponse(raw: unknown): StoneFindingResponse {
  if (typeof raw !== "object" || raw === null)
    throw new StoneContractError("response not an object");
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.stones)) throw new StoneContractError("stones[] missing");
  return {
    studyInstanceUid: str(r.studyInstanceUid, "studyInstanceUid"),
    modelVersion: str(r.modelVersion, "modelVersion"),
    generatedAt: str(r.generatedAt, "generatedAt"),
    stones: r.stones.map(parseStone),
  };
}
